import React from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE } from 'api/utilityActionTypes';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';

import './UpSelfOnboarding.less';

const options = [
  {
    label: 'Call support.',
    errorMessage: 'Not the best option...',
  },
  {
    label: 'Yell into the heavens.',
    errorMessage: 'Maybe at first... but that’s not it.',
  },
  {
    label: 'Do nothing.',
    errorMessage: 'Nooo.... there’s always something.',
  },
  {
    label: (
      <>
        Disable the water in the
        <br /> “Synced Listings” section.
      </>
    ),
  },
];

const getFormFields = ({ formValues = {} }) => {
  const formFields = [
    {
      id: 'answer',
      title: (
        <>
          Pop quiz: If water is included in
          <br /> the rent, how do you set that up?
        </>
      ),
      type: 'multiple_choice',
      properties: {
        choices: options.map(({ label, errorMessage }, index) => ({
          value: index,
          label: label,
          errorMessage: formValues.answer === index ? errorMessage : null,
        })),
        multipleSelection: false,
        fieldType: SR_CHECKBOX_THEME_BOX,
        autoFitWidth: '100%',
        checkboxClassName: 'selfOnboardingCheckbox',
      },
      validations: ['required'],
    },
    {
      id: 'requireUtilitiesForKeys',
      title: 'Survey: Will you hold the keys until the tenant completes the utilities?',
      type: 'yes_no',
      validations: ['required'],
      properties: {
        className: 'selfOnboardingcheckboxListBoolean',
      },
    },
  ];
  return formFields;
};

const UpSelfOnboardingIntroStep = ({
  activeIndex,
  utilityPartnerGroup,
  utilityPartner,
  onNext,
}) => {
  const formFields = getFormFields({});
  const dispatch = useDispatch();
  const theme = utilityPartnerGroup?.defaultTheme || {};
  const groupOnboardingOverrides = utilityPartnerGroup?.onboardingOverrides || {};
  const onboardingVideoUrl =
    groupOnboardingOverrides?.introSectionVideoUrl ||
    'https://www.youtube.com/embed/nNnSFL7qH-s?autoplay=1&rel=0';

  const onSubmit = (formValues) => {
    if (formValues.answer === 3) {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE,
        payload: {
          utilityPartnerId: utilityPartner.id,
          updates: {
            requireUtilitiesForKeys: formValues.requireUtilitiesForKeys,
          },
        },
        onSuccess: () => {
          onNext(formValues);
        },
      });
    }
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          formValues: formikBag.values,
        });

        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                <span styleName="titleText">Watch a quick intro video 👉</span>
                <span styleName="subTitleText">It's useful, simple, and painless!</span>
                <SrBox margin="26px 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  hasBoxShadow
                  type="submit"
                  themeOverrides={{
                    backgroundColor: theme?.buttonPrimaryColor,
                    color: theme?.buttonPrimaryTextColor,
                  }}
                >
                  Continue
                </SrButton>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content introStep fadeIn">
                <div styleName="row">
                  <span styleName="titleText">
                    {groupOnboardingOverrides?.introSectionVideoTitle || 'Quick onboarding video:'}
                  </span>
                  <div styleName="videoBox">
                    <iframe
                      title="video"
                      width="740px"
                      height="460px"
                      src={onboardingVideoUrl}
                      allow="accelerometer; autoplay; clipboard-write;"
                      allowFullScreen
                      frameBorder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingIntroStep.displayName = 'UpSelfOnboardingIntroStep';

UpSelfOnboardingIntroStep.propTypes = {
  activeIndex: PropTypes.number,
  utilityPartnerGroup: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  utilityPartner: PropTypes.object.isRequired,
};

export default UpSelfOnboardingIntroStep;
